import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Shield,
  Search,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Store,
  CreditCard,
  Settings,
  FileText,
  Clock,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const severityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700'
};

const categoryIcons = {
  auth: User,
  admin: Shield,
  restaurant: Store,
  order: FileText,
  payment: CreditCard,
  data: Settings
};

const categoryColors = {
  auth: 'text-blue-600 bg-blue-50',
  admin: 'text-purple-600 bg-purple-50',
  restaurant: 'text-green-600 bg-green-50',
  order: 'text-orange-600 bg-orange-50',
  payment: 'text-emerald-600 bg-emerald-50',
  data: 'text-slate-600 bg-slate-50'
};

function AuditLogs() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('logs');

  // Fetch audit logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['audit-logs', page, search, actionFilter, severityFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '25'
      });
      if (search) params.append('search', search);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      
      const { data } = await axios.get(`${BACKEND_URL}/api/audit/logs?${params}`);
      return data;
    }
  });

  // Fetch audit stats
  const { data: statsData } = useQuery({
    queryKey: ['audit-stats'],
    queryFn: async () => {
      const { data } = await axios.get(`${BACKEND_URL}/api/audit/stats?days=30`);
      return data;
    }
  });

  // Fetch available actions
  const { data: actionsData } = useQuery({
    queryKey: ['audit-actions'],
    queryFn: async () => {
      const { data } = await axios.get(`${BACKEND_URL}/api/audit/actions`);
      return data;
    }
  });

  const handleExport = async () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const startDate = thirtyDaysAgo.toISOString().split('T')[0];
    const endDate = today.toISOString().split('T')[0];
    
    window.open(
      `${BACKEND_URL}/api/audit/export?start_date=${startDate}&end_date=${endDate}&format=csv`,
      '_blank'
    );
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'dd MMM yyyy HH:mm:ss', { locale: fr });
    } catch {
      return timestamp;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'auth.login.success': 'Connexion réussie',
      'auth.login.failure': 'Échec connexion',
      'auth.register': 'Inscription',
      'auth.password_reset.request': 'Demande réinit. mot de passe',
      'admin.restaurant.approve': 'Approbation restaurant',
      'admin.restaurant.reject': 'Rejet restaurant',
      'admin.settings.update': 'Modification paramètres',
      'restaurant.dish.create': 'Création plat',
      'restaurant.dish.update': 'Modification plat',
      'restaurant.settings.update': 'Modification paramètres resto',
      'order.create': 'Création commande',
      'order.status.change': 'Changement statut commande',
      'order.refund': 'Remboursement',
      'payment.success': 'Paiement réussi',
      'payment.failure': 'Échec paiement'
    };
    return labels[action] || action;
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-purple-600" />
            Journal d'Audit
          </h1>
          <p className="text-gray-500 mt-1">
            Traçabilité des actions sensibles sur la plateforme
          </p>
        </div>
        <Button onClick={handleExport} variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Exporter (30j)
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="logs" className="gap-2">
            <FileText className="h-4 w-4" />
            Logs
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Statistiques
          </TabsTrigger>
        </TabsList>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-6">
          {statsData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{statsData.total_events?.toLocaleString()}</div>
                    <p className="text-sm text-gray-500">Événements (30j)</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-red-600">
                      {statsData.by_severity?.critical || 0}
                    </div>
                    <p className="text-sm text-gray-500">Critiques</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-orange-600">
                      {statsData.by_severity?.high || 0}
                    </div>
                    <p className="text-sm text-gray-500">Haute importance</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">
                      {statsData.by_category?.auth || 0}
                    </div>
                    <p className="text-sm text-gray-500">Connexions</p>
                  </CardContent>
                </Card>
              </div>

              {/* Category Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Répartition par catégorie</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    {Object.entries(statsData.by_category || {}).map(([category, count]) => {
                      const Icon = categoryIcons[category] || FileText;
                      return (
                        <div
                          key={category}
                          className={`p-4 rounded-lg ${categoryColors[category] || 'bg-gray-50'}`}
                        >
                          <Icon className="h-5 w-5 mb-2" />
                          <div className="text-xl font-bold">{count}</div>
                          <div className="text-sm capitalize">{category}</div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top Failures */}
              {statsData.top_failures?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      Échecs les plus fréquents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {statsData.top_failures.map((failure, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <span className="text-sm">{getActionLabel(failure.action)}</span>
                          <Badge variant="destructive">{failure.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Rechercher..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      className="pl-10"
                      data-testid="audit-search-input"
                    />
                  </div>
                </div>
                <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes catégories</SelectItem>
                    <SelectItem value="auth">Authentification</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="restaurant">Restaurant</SelectItem>
                    <SelectItem value="order">Commandes</SelectItem>
                    <SelectItem value="payment">Paiements</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Sévérité" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="low">Basse</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                    <SelectItem value="critical">Critique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card>
            <CardContent className="p-0">
              {logsLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Horodatage
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Action
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Acteur
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Cible
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Sévérité
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Statut
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {logsData?.logs?.map((log) => {
                          const CategoryIcon = categoryIcons[log.action_category] || FileText;
                          return (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-gray-400" />
                                  {formatTimestamp(log.timestamp)}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className={`p-1.5 rounded ${categoryColors[log.action_category] || ''}`}>
                                    <CategoryIcon className="h-4 w-4" />
                                  </span>
                                  <div>
                                    <div className="text-sm font-medium">
                                      {getActionLabel(log.action)}
                                    </div>
                                    <div className="text-xs text-gray-500">{log.action}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm">
                                  {log.actor?.email || 'Anonyme'}
                                </div>
                                {log.actor?.role && (
                                  <Badge variant="outline" className="text-xs mt-1">
                                    {log.actor.role}
                                  </Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {log.target?.name || log.target?.type || '-'}
                              </td>
                              <td className="px-4 py-3">
                                <Badge className={severityColors[log.severity]}>
                                  {log.severity}
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                {log.success ? (
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                ) : (
                                  <XCircle className="h-5 w-5 text-red-500" />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {(!logsData?.logs || logsData.logs.length === 0) && (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                              Aucun log trouvé
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {logsData && logsData.total_pages > 1 && (
                    <div className="flex items-center justify-between p-4 border-t">
                      <div className="text-sm text-gray-500">
                        Page {logsData.page} sur {logsData.total_pages} ({logsData.total} résultats)
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(p => Math.min(logsData.total_pages, p + 1))}
                          disabled={page === logsData.total_pages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default AuditLogs;
